// src/motomate-widget.js
import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";

/** Resolve config from element attributes, window.MotoMate.config, or URL params */
function resolveConfig(el) {
  const urlParams = new URLSearchParams(window.location.search);
  const globalCfg = (window.MotoMate && window.MotoMate.config) || {};
  const attr = (k) => el.getAttribute(k);

  const pick = (k, fallback) => {
    const viaAttr = attr(k);
    if (viaAttr !== null) return viaAttr;
    if (globalCfg[k] !== undefined && globalCfg[k] !== null)
      return globalCfg[k];
    if (urlParams.has(k)) return urlParams.get(k);
    return fallback;
  };

  const reservedRaw = pick("reserved", false);
  const reserved =
    typeof reservedRaw === "string"
      ? ["1", "true", "yes"].includes(reservedRaw.toLowerCase())
      : Boolean(reservedRaw);

  return {
    vehicleReg: pick("vehicleReg", ""),
    reserved,
    description: pick("description", ""),
  };
}

/** Minimal Shadow DOM template: chat body only */
function shadowTemplate() {
  const style = `
    :host { all: initial; }
    *, *::before, *::after { box-sizing: border-box; }

    .mm-wrap {
      all: initial;
      font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
      display: block;
      height: 100%;
    }

    .mm-body {
      display: block;
      width: 100%;
      height: 100%;
      overflow: hidden;
      background: #ffffff;
    }

    .mount {
      width: 100%;
      height: 100%;
    }
  `;

  const html = `
    <div class="mm-wrap">
      <div class="mm-body">
        <div class="mount"></div>
      </div>
    </div>
  `;

  return { style, html };
}

class MotoMateElement extends HTMLElement {
  constructor() {
    super();
    this._shadow = null;
    this._reactRoot = null;
    this._mount = null;
  }

  connectedCallback() {
    // Optional: let the parent loader know we’re ready (harmless if unused)
    try {
      if (window.parent) {
        window.parent.postMessage({ type: "MOTOMATE_WIDGET_READY" }, "*");
      }
    } catch {}

    // Mount Shadow DOM
    this._shadow = this.attachShadow({ mode: "open" });
    const { style, html } = shadowTemplate();

    const styleTag = document.createElement("style");
    styleTag.textContent = style;
    this._shadow.appendChild(styleTag);

    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;
    this._shadow.appendChild(wrapper);

    // React mount
    this._mount = this._shadow.querySelector(".mount");
    const cfg = resolveConfig(this);
    this._reactRoot = createRoot(this._mount);
    this._reactRoot.render(<App {...cfg} />);
  }

  disconnectedCallback() {
    if (this._reactRoot) {
      try {
        this._reactRoot.unmount();
      } catch {}
      this._reactRoot = null;
    }
  }
}

/** Register element */
export function registerMotoMate() {
  if (!customElements.get("motomate-chat")) {
    customElements.define("motomate-chat", MotoMateElement);
  }
}
if (typeof window !== "undefined" && window.customElements) {
  registerMotoMate();
}
